import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiX, FiHeart } = FiIcons;

export default function AboutPage({ onBack, scrollToSupport }) {
  const [supportMode, setSupportMode] = useState('one-time');
  const supportRef = useRef(null);
  const scrollContainerRef = useRef(null);

  // 優化後的捲動邏輯：直接對容器進行精確捲動
  useEffect(() => {
    if (scrollToSupport && supportRef.current && scrollContainerRef.current) {
      const performScroll = () => {
        const container = scrollContainerRef.current;
        const target = supportRef.current;
        
        if (container && target) {
          // 精確計算目標距離頂部的位置，並預留一點空間 (20px)
          container.scrollTo({
            top: target.offsetTop - 20,
            behavior: 'smooth'
          });
        }
      };

      // 設定兩個時間點觸發，確保無論是快速載入還是等待 Framer Motion 動畫，都能準確捲動
      const timer1 = setTimeout(performScroll, 100);
      const timer2 = setTimeout(performScroll, 500);
      
      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
      };
    }
  }, [scrollToSupport]);

  const supportOptions = [
    { 
      amount: 5, 
      label: '一杯咖啡', 
      oneTime: 'https://buy.stripe.com/3cI4gA3ez5SJa1N12a0Fi1i', 
      monthly: 'https://buy.stripe.com/8x2bJ2bL580R1vh7qy0Fi1e' 
    },
    { 
      amount: 8, 
      label: '一份心意', 
      oneTime: 'https://buy.stripe.com/7sYeVecP90yp2zleT00Fi1f', 
      monthly: 'https://buy.stripe.com/00waEY9CXch75Lx9yG0Fi1g' 
    },
    { 
      amount: 10, 
      label: '溫暖支持', 
      oneTime: 'https://buy.stripe.com/3cIfZi4iD80R8XJfX40Fi1h', 
      monthly: 'https://buy.stripe.com/5kQcN602nepfei3fX40Fi1j' 
    },
  ];

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.8 }}
      className="relative z-10 w-full max-w-2xl px-6 py-8 md:py-12 h-full flex flex-col"
    >
      {/* 頂部導覽列 */}
      <div className="shrink-0 relative flex items-center justify-center mb-8 min-h-[3.5rem]">
        <button 
          onClick={onBack}
          className="absolute right-0 text-ink-brown/50 hover:text-ink-brown transition-colors p-2"
          aria-label="關閉"
        >
          <SafeIcon icon={FiX} className="w-6 h-6" />
        </button>
      </div>

      {/* 內容滾動區：加入 scrollContainerRef 及 relative 確保定位準確 */}
      <div ref={scrollContainerRef} className="flex-1 overflow-y-auto no-scrollbar pb-12 relative scroll-smooth">
        <div className="space-y-12">
          
          {/* 標題區 */}
          <div className="text-center space-y-4">
            <h1 className="font-brush text-5xl md:text-6xl text-ink-brown opacity-85 tracking-widest pl-4">
              關於輕舟
            </h1>
            <p className="font-handwriting text-ink-brown/50 text-sm tracking-[0.3em]">
              Drift
            </p>
          </div>

          {/* 命名由來 */}
          <section className="space-y-6">
            <div className="text-center">
              <span className="font-handwriting text-lg md:text-xl text-ink-brown tracking-[0.2em] relative inline-block">
                「輕舟已過萬重山。」
                <div className="absolute -bottom-2 left-0 w-full h-[1px] bg-khaki"></div>
              </span>
            </div>
            
            <div className="font-handwriting text-ink-brown/80 text-sm md:text-base leading-loose tracking-widest space-y-4 text-justify px-2 md:px-8">
              <p>
                這句話，是「輕舟」誕生的起點。
              </p>
              <p>
                我們的心裡，總有許多搬不開的石頭、跨不過的萬重山。那些無法對人言說的壓力、遺憾與傷痛，日積月累，壓得人喘不過氣。
              </p>
              <p>
                「輕舟」是一個專為承載情緒而生的樹洞。在這裡，你可以卸下所有偽裝。把那些沉重的思緒，摺成一葉輕飄飄的紙船，讓它代替你，去承受那些重量。
              </p>
            </div>
          </section>

          {/* 使用說明 */}
          <section className="space-y-6 px-2 md:px-8">
            <h2 className="font-handwriting text-ink-brown/60 text-sm tracking-[0.3em] text-center mb-6">
              — 湖畔指南 —
            </h2>
            <div className="space-y-6 font-handwriting text-ink-brown/80 text-sm md:text-base leading-loose tracking-widest">
              <div className="flex gap-4">
                <span className="shrink-0 text-ink-red/60 font-brush text-xl">一</span>
                <p><strong className="font-semibold text-ink-brown">寫下：</strong>選擇一個符合當下心境的分類，將想說的話毫無保留地傾吐。</p>
              </div>
              <div className="flex gap-4">
                <span className="shrink-0 text-ink-red/60 font-brush text-xl">二</span>
                <p><strong className="font-semibold text-ink-brown">放手：</strong>點擊發送，看著你的心事被溫柔地摺成紙船，駛入湖面的迷霧之中。</p>
              </div>
              <div className="flex gap-4">
                <span className="shrink-0 text-ink-red/60 font-brush text-xl">三</span>
                <p><strong className="font-semibold text-ink-brown">消散：</strong>紙船遠去後，水面歸於平靜。深呼吸，回到日常。</p>
              </div>
            </div>
          </section>

          {/* 隱私聲明 */}
          <section className="space-y-6 px-2 md:px-8 bg-white/30 rounded-xl p-6 border border-khaki-light/40">
            <h2 className="font-handwriting text-ink-brown/60 text-sm tracking-[0.3em] text-center">
              — 落水無痕，隨波消散 —
            </h2>
            <div className="font-handwriting text-ink-brown/80 text-sm md:text-base leading-loose tracking-widest text-justify space-y-4">
              <p>
                這裡沒有記憶，也沒有過往。
              </p>
              <p>
                如同紙船終將浸濕、沉入湖底，你寫下的每一寸字句，都會在駛入迷霧後隨波消散，不留一絲痕跡。
              </p>
              <p>
                這是一場專屬於你的情緒告別儀式。
              </p>
            </div>
          </section>

          {/* 支持我們 (Stripe 整合區塊) */}
          <section ref={supportRef} className="space-y-6 px-2 md:px-8 pt-6">
            <h2 className="font-handwriting text-ink-brown/60 text-sm tracking-[0.3em] text-center mb-6">
              — 支持輕舟 —
            </h2>
            <div className="font-handwriting text-ink-brown/80 text-sm md:text-base leading-loose tracking-widest text-center space-y-6">
              <p className="text-justify md:text-center">
                輕舟是一個無廣告、不追蹤數據的獨立專案。<br className="hidden md:block"/>
                如果這裡的湖水曾溫柔地接住你的情緒，歡迎支持我們，讓這艘小船能持續在網路上航行。
              </p>

              {/* 切換按鈕 */}
              <div className="flex justify-center gap-3 pt-2">
                <button
                  onClick={() => setSupportMode('one-time')}
                  className={`px-6 py-2.5 rounded-full text-sm tracking-[0.2em] transition-all duration-500 ${supportMode === 'one-time' ? 'bg-ink-brown text-[#FDFBF7] shadow-md' : 'bg-white/40 text-ink-brown/60 hover:bg-white/70 border border-khaki-light/50'}`}
                >
                  單次支持
                </button>
                <button
                  onClick={() => setSupportMode('monthly')}
                  className={`px-6 py-2.5 rounded-full text-sm tracking-[0.2em] transition-all duration-500 ${supportMode === 'monthly' ? 'bg-ink-brown text-[#FDFBF7] shadow-md' : 'bg-white/40 text-ink-brown/60 hover:bg-white/70 border border-khaki-light/50'}`}
                >
                  每月訂閱
                </button>
              </div>

              {/* 金額選項卡片 */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4">
                {supportOptions.map((opt) => (
                  <a
                    key={opt.amount}
                    href={supportMode === 'one-time' ? opt.oneTime : opt.monthly}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex flex-col items-center justify-center gap-3 p-6 bg-white/40 border border-khaki-light hover:bg-khaki-light/30 hover:border-ink-brown/30 rounded-2xl transition-all duration-500 shadow-[0_2px_10px_rgba(0,0,0,0.02)] active:scale-95 group relative overflow-hidden"
                  >
                    {/* 裝飾背景 */}
                    <div className="absolute -right-4 -top-4 w-16 h-16 bg-khaki-light/20 rounded-full blur-xl group-hover:bg-ink-red/5 transition-colors duration-500"></div>

                    <SafeIcon icon={FiHeart} className="w-5 h-5 text-ink-red/40 group-hover:text-ink-red/70 group-hover:scale-110 transition-all duration-500" />
                    
                    <div className="flex flex-col items-center gap-1">
                      <span className="font-brush text-3xl text-ink-brown opacity-90">${opt.amount}</span>
                      <span className="text-[10px] tracking-widest text-ink-brown/50 font-sans uppercase">
                        USD / {supportMode === 'one-time' ? 'One-time' : 'Monthly'}
                      </span>
                    </div>
                    
                    <span className="text-sm text-ink-brown/80 tracking-widest mt-1 border-t border-khaki-light/50 pt-3 w-full text-center">
                      {opt.label}
                    </span>
                  </a>
                ))}
              </div>

            </div>
          </section>

        </div>
      </div>
    </motion.div>
  );
}